import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  fetchWorkers,
  deleteWorker,
  selectAllWorkers,
  selectWorkersPageInfo,
  selectWorkersLoading,
  selectWorkersInitialFetchDone,
  selectWorkersFilters,
  setFilters
} from 'store/slices/workersSlice';
import { WORKERS_PAGE_SIZE, WORKER_TYPES_OPTIONS } from 'constants/workers';
import Page from 'components/layout/Page/Page';
import { PrimaryButton } from 'components/buttons';
import Modal from 'components/layout/modals/Modal/Modal';
import DeleteModal from 'components/layout/modals/DeleteModal/DeleteModal';
import { FilterBar, useFilterBar } from 'components/filter';
import WorkerTabs from './components/WorkerTabs/WorkerTabs';
import { CardHeader } from 'components/cards';
import { StackedList } from 'components/stackedLists';
import WorkerStackedListItem from './components/WorkerStackedListItem/WorkerStackedListItem';
import AttachWorkerToReminder from './components/AttachWorkerToReminder/AttachWorkerToReminder';

const WORKER_FILTER_OPTIONS = [
  { value: 'ALL', label: 'All' },
  ...WORKER_TYPES_OPTIONS,
];

const WORKER_LIST_FILTERS = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Search by worker name',
    tier: 'primary',
  },
  {
    key: 'type',
    type: 'select',
    label: 'Type',
    options: WORKER_FILTER_OPTIONS,
    tier: 'primary',
  },
];

const WORKER_LIST_FILTER_CONFIG = {
  route: '/workers',
  filters: {
    type: {
      type: 'select',
      urlKey: 'type',
      default: 'ALL',
      options: WORKER_FILTER_OPTIONS,
    },
    search: {
      type: 'search',
      urlKey: 'search',
      default: '',
    },
  },
  pagination: {
    pageSize: WORKERS_PAGE_SIZE,
  },
  fetchThunk: fetchWorkers,
  setFilters,
  selectFilters: selectWorkersFilters,
  selectLoading: selectWorkersLoading,
  selectInitialFetchDone: selectWorkersInitialFetchDone,
};

function Workers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const workers = useSelector(selectAllWorkers);
  const pageInfo = useSelector(selectWorkersPageInfo);
  const loading = useSelector(selectWorkersLoading);
  const user = useSelector((state) => state.auth.user);
  const {
    filterValues,
    isInitialLoading,
    hasActiveFilters,
    applyFilters,
    resetFilters,
    handlePageChange,
  } = useFilterBar(WORKER_LIST_FILTER_CONFIG);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState(null);
  const [deleteWorkerName, setDeleteWorkerName] = useState(null);

  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [workerToAttach, setWorkerToAttach] = useState(null);

  const handleDeleteClick = (worker) => {
    setWorkerToDelete(worker);
    setDeleteWorkerName(worker?.name || null);
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
    setWorkerToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (workerToDelete) {
      try {
        await dispatch(deleteWorker(workerToDelete._id)).unwrap();
        handleDeleteClose();
      } catch (error) {
        console.error('Failed to delete worker:', error);
      }
    }
  };

  const handleAction = (action, item) => {
    const id = item._id;
    switch (action) {
      case 'view':
        // Assuming view navigates to edit or details page
        navigate(`view?id=${id}`);
        break;
      case 'logs':
        navigate(`logs?workerId=${id}`);
        break;
      case 'edit':
        navigate(`edit?id=${id}`);
        break;
      case 'delete':
        handleDeleteClick(item);
        break;
      case 'add-worker':
        setWorkerToAttach(item);
        setAttachModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleAttachSuccess = () => {
      setAttachModalOpen(false);
      setWorkerToAttach(null);
      // Optional: Show success toast
  };

  return (
    <Page title="Workers" headerActions={<WorkerTabs />}>
      <CardHeader
        leftAction={
          <FilterBar
            filters={WORKER_LIST_FILTERS}
            values={filterValues}
            onChange={applyFilters}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
            loading={isInitialLoading}
          />
        }
        rightAction={
          <PrimaryButton
            icon={PlusCircleIcon}
            loading={isInitialLoading}
            onClick={() => navigate('add')}
          >
            Create worker
          </PrimaryButton>
        }
      >
        <StackedList
          items={workers}
          settings={user?.settings}
          onAction={handleAction}
          itemComponent={WorkerStackedListItem}
          emptyMessage="No workers found."
          pagination={pageInfo ? {
            pageIndex: pageInfo.page - 1,
            pageSize: pageInfo.limit,
            totalItems: pageInfo.totalDocs,
            onPageChange: handlePageChange
          } : undefined}
          loading={isInitialLoading}
        />
      </CardHeader>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteClose}
        title="Delete Worker"
        message={`Are you sure you want to delete worker "${workerToDelete?.name || deleteWorkerName}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        loading={loading}
      />

      <Modal
        isOpen={attachModalOpen}
        onClose={() => setAttachModalOpen(false)}
        className="sm:max-w-xl"
      >
        <div className="relative w-full h-auto flex flex-col bg-gray-800 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
          <button
            type="button"
            onClick={() => setAttachModalOpen(false)}
            className="absolute right-4 top-4 rounded-md text-gray-400 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <span className="sr-only">Close</span>
            <XMarkIcon className="h-6 w-6 cursor-pointer" aria-hidden="true" />
          </button>
          <h3 className="mb-4 text-base font-semibold leading-6 text-white">Attach Worker to Reminder</h3>
          <AttachWorkerToReminder
            workerId={workerToAttach?._id}
            onSuccess={handleAttachSuccess}
            onCancel={() => setAttachModalOpen(false)}
            showWorkerInfo={true}
            className="flex-1"
          />
        </div>
      </Modal>
    </Page>
  );
}

export default Workers;
